import { useCallback, useMemo } from "react";

import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { duplicateDocument, type DocumentShort } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { docPageUrl } from "@/urls";
import { attributedCopyTitle } from "@/utils/attributedCopyTitle";
import { trackEvent } from "@/utils/trackEvent";

import { DownloadPDFButton } from "../../right-sidebar/download-menu/DownloadPDFButton";
import { ExportToOverleafButton } from "../../right-sidebar/download-menu/ExportToOverleafButton";

import { LockEditingButton } from "./LockEditingButton";
import { useCreateInTree } from "./useCreateInTree";

import type { RenderContext } from "./FolderItem";
import type { ContextMenuItem } from "@/components/sidebar/context-menu/ContextMenuPanel";
import type { DropdownItemRenderProps } from "@/components/sidebar/Dropdown";
import type { DocumentTreeStore } from "@/store/DocumentTreeStore";

export function useDocumentItemMenuItems({
  documentTree,
  docItem,
  uid,
  renderContext,
  onLockedChange,
}: {
  documentTree: DocumentTreeStore;
  docItem: DocumentShort;
  uid: string;
  renderContext?: RenderContext;
  onLockedChange?: (locked: boolean) => void;
}): ContextMenuItem[] {
  const router = useRouter();
  const t = useTranslations();
  const { auth, classroom, dialog } = useStore();
  const { createDocumentInTree, createFolderInTree } = useCreateInTree("document");
  const isStudent = classroom.isStudentIn(documentTree.team);

  const handleCreateNewDocumentBelow = useCallback(async () => {
    // TODO: handle error here
    const { folder: parentFolder, currentIndex = 0 } = documentTree.getParentFolder(docItem);
    await createDocumentInTree(parentFolder, currentIndex + 2);
  }, [createDocumentInTree, docItem, documentTree]);

  const handleCreateNewFolderBelow = useCallback(async () => {
    // TODO: handle error here
    const { folder: parentFolder, currentIndex = 0 } = documentTree.getParentFolder(docItem);
    await createFolderInTree(parentFolder, currentIndex + 2);
  }, [createFolderInTree, docItem, documentTree]);

  const handleDeleteDocument = useCallback(() => {
    if (!documentTree.trash) {
      return;
    }
    if (documentTree.currentDocId === uid) {
      const adjacentDocument = documentTree.getAdjacentDocument(docItem);
      if (adjacentDocument) {
        router.push(docPageUrl({ docId: adjacentDocument.uid }));
      }
    }
    documentTree.updateFilePosition(uid, documentTree.trash.uid, 0);
  }, [docItem, documentTree, router, uid]);

  const handleDuplicateDocument = useCallback(async () => {
    // TODO: handle error here
    const { folder: parentFolder, currentIndex = 0 } = documentTree.getParentFolder(docItem);

    // Without a parent the API puts the copy in the user's Drafts, and rejects a bare position.
    const { document: newDocument } = await duplicateDocument(
      docItem.uid,
      parentFolder ? { parentUid: parentFolder.uid, position: currentIndex + 2 } : {},
    );
    if (newDocument) {
      const userName = auth.currentUser?.name;
      if (userName) {
        documentTree.setPendingCopyTitle(
          newDocument.uid,
          attributedCopyTitle(docItem.title || t("doc.documentTitle.placeholder"), userName),
        );
      }
      trackEvent("document_creation_clicked", { source: "duplicate" });
      router.push(docPageUrl({ docId: newDocument.uid }));
    }
  }, [auth, docItem, documentTree, router, t]);

  const handleForkDocument = useCallback(async () => {
    // TODO: handle error here
    trackEvent("fork_document_clicked", { doc_id: docItem.uid, source: "document" });
    const forkedDocument = await dialog.forkDialogStore.handleForkButtonClick({
      docUidToFork: docItem.uid,
    });
    if (forkedDocument) {
      router.push(docPageUrl({ docId: forkedDocument.uid }));
    }
  }, [docItem, dialog.forkDialogStore, router]);

  const handleCopyLink = useCallback(async () => {
    const url = docPageUrl({ docId: docItem.uid }, { host: true });
    await navigator.clipboard.writeText(url);
    trackEvent("copy_link_clicked", { doc_id: docItem.uid, source: "document" });
  }, [docItem.uid]);

  // Recent keeps docs the tree hides, like another member's shared draft, and deleting moves a tree item.
  const isInTree = !!documentTree.itemById(uid);

  const personalDocTreeMenuItems = useMemo(
    () =>
      [
        {
          title: t("documentTree.duplicateDocument"),
          icon: <Icon name="Duplicate" />,
          onClick: handleDuplicateDocument,
        },
        ...(!isStudent
          ? [
              {
                title: t("documentTree.forkDocument"),
                icon: <Icon name="Fork" />,
                onClick: handleForkDocument,
              },
              ...(docItem.canToggleLock
                ? [
                    {
                      renderItem: (props: DropdownItemRenderProps) => (
                        <LockEditingButton
                          {...props}
                          uid={docItem.uid}
                          locked={!!docItem.locked}
                          onLockedChange={onLockedChange}
                        />
                      ),
                    },
                  ]
                : []),
            ]
          : []),
        { type: "separator" },
        {
          title: t("documentTree.copyLink"),
          icon: <Icon name="Link" />,
          onClick: handleCopyLink,
        },
        {
          renderItem: (props) => <DownloadPDFButton {...props} docId={docItem.uid} />,
        },
        {
          renderItem: (props) => (
            <ExportToOverleafButton
              {...props}
              docId={docItem.uid}
              docPermissions={docItem.userPermissions}
            />
          ),
        },
        // Students create nothing except duplicates (their copy is stamped
        // and stays private) and never delete
        // (docs/plans/student-permissions.md).
        ...(!isStudent
          ? [
              // Recent rows don't show the doc's place in the tree, so "below" has no meaning there.
              ...(renderContext === "document-tree"
                ? [
                    { type: "separator" } as const,
                    {
                      title: t("documentTree.addDocumentBelow"),
                      icon: <Icon name="Add" />,
                      onClick: handleCreateNewDocumentBelow,
                    },
                    {
                      title: t("documentTree.addFolderBelow"),
                      icon: <Icon name="AddFolder" />,
                      onClick: handleCreateNewFolderBelow,
                    },
                  ]
                : []),
              ...(isInTree
                ? [
                    { type: "separator" } as const,
                    {
                      title: t("documentTree.deleteDocument"),
                      icon: <Icon name="Trash" />,
                      onClick: handleDeleteDocument,
                    },
                  ]
                : []),
            ]
          : []),
      ] satisfies ContextMenuItem[],
    [
      t,
      handleCopyLink,
      handleForkDocument,
      handleCreateNewDocumentBelow,
      handleCreateNewFolderBelow,
      handleDuplicateDocument,
      handleDeleteDocument,
      docItem,
      onLockedChange,
      isStudent,
      renderContext,
      isInTree,
    ],
  );

  const publicProfileDocTreeMenuItems = useMemo(
    () =>
      [
        {
          title: t("documentTree.forkDocument"),
          icon: <Icon name="Fork" />,
          onClick: handleForkDocument,
        },
        { type: "separator" },
        {
          title: t("documentTree.copyLink"),
          icon: <Icon name="Link" />,
          onClick: handleCopyLink,
        },
        {
          renderItem: (props) => <DownloadPDFButton {...props} docId={docItem.uid} />,
        },
        {
          renderItem: (props) => (
            <ExportToOverleafButton
              {...props}
              docId={docItem.uid}
              docPermissions={docItem.userPermissions}
            />
          ),
        },
      ] satisfies ContextMenuItem[],
    [t, handleCopyLink, handleForkDocument, docItem.uid, docItem.userPermissions],
  );

  if (renderContext === "document-tree" || renderContext === "recent") {
    return personalDocTreeMenuItems;
  }

  if (renderContext === "public-profile") {
    return publicProfileDocTreeMenuItems;
  }

  return [];
}
